import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useFetchProjectInsights } from "@/app/hooks/fetchs/project/useFetchProjectInsights";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import {
  CallAnalysisCategory,
  ProjectCallsRecommendationKind,
} from "@/app/types/call/CallAnalysis";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UCallScope } from "@/app/utils/UCallScope";
import { Limits } from "@/app/vars/Limits";
import styles from "./Analysis.module.css";
import AnalysisCategory from "./components/AnalysisCategory/AnalysisCategory";
import AnalysisRecommendations from "./components/AnalysisRecommendations/AnalysisRecommendations";

type Props = DefaultProps & {
  filter?: CallScopeFilter;
  callsLoading?: boolean;
  enabled?: boolean;
};

export default function AnalysisMarket(props: Props) {
  const { params, filter = {}, callsLoading = false, enabled = true } = props;
  const user = useAuthUser();
  const project = ProjectStore((state) => state.currentProject);
  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const { data: insights, isLoading } = useFetchProjectInsights(
    project?.id,
    currentOfferId,
    filter,
    enabled,
  );

  if (isLoading || callsLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}>
        <PointsLoader
          centered
          color="var(--color-green-3)"
          delay={Limits.CALL_ANALYSIS_LOADER_DELAY}
        />
      </div>
    );
  }

  const pains = insights?.PAIN_POINT;
  const objections = insights?.OBJECTION;
  const signals = insights?.BUYING_SIGNAL;
  const verbatims = insights?.VERBATIM;
  const features = insights?.FEATURE_REQUEST;
  const competitors = insights?.COMPETITOR_MENTION;

  const hasAnyInsights = insights && Object.values(insights).some((cat) => cat.totalCount > 0);

  if (user && (!project || !hasAnyInsights)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.PAIN}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_PAIN}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_PAIN_SUB}
          data={pains}
        />
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.OBJECTIONS}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_OBJECTION}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_OBJECTION_SUB}
          data={objections}
        />
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.SIGNALS}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_SIGNALS}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_SIGNALS_SUB}
          data={signals}
        />
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.VERBATIMS}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_VERBATIMS}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_VERBATIMS_SUB}
          data={verbatims}
        />
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.PRODUCT}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_FEATURES}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_FEATURES_SUB}
          data={features}
        />
        <AnalysisCategory
          params={params}
          filter={filter}
          category={CallAnalysisCategory.COMPETITORS}
          titleKey={TKey.CALL_ANALYSIS_INSIGHT_COMPETITORS}
          subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_COMPETITORS_SUB}
          data={competitors}
        />
      </div>

      {!UCallScope.isFiltered(filter) && (
        <div className={styles.rightColumn}>
          <AnalysisRecommendations params={params} kind={ProjectCallsRecommendationKind.MARKET} />
        </div>
      )}
    </div>
  );
}
